import React from 'react';
import { useSelector } from 'react-redux';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import Icon from 'yoda-core-components/lib/components/Icon/Icon';
import Marketplace from 'yoda-core-components/lib/assets/icons/utility/Marketplace.svg';
import { selectFeatureFlags } from 'yoda-site-components/lib/selectors/ContextSelector';
import { selectMarketPlaceSellerInfo } from 'yoda-site-components/lib/selectors/MarketPlaceSelector';
import ErrorBoundary from 'yoda-site-components/lib/components/ErrorBoundary/ErrorBoundary';
import ProductConstants from '../../helpers/ProductConstants';

const SellerInformationContent = ({ sellerData }) => {
    const { sellerName, sellerAddress, sellerPhone, sellerEmail } = sellerData;

    return (
        <div data-automation-id="seller-information-content">
            <div data-automation-id="seller-info-block">
                <span
                    className={dt(['w-6', 'h-6', 'inline-block', 'text-center'])}
                    data-automation-id="seller-info-icon"
                >
                    <Icon iconType="svg" width="24px" height="24px" viewBox="0 0 18 16">
                        <Marketplace />
                    </Icon>
                </span>
                <span
                    className={dt([
                        'inline-block',
                        'text-base',
                        'text-left',
                        'pl-2',
                        'font-open-sans-semibold',
                        'text-black',
                    ])}
                    data-automation-id="seller-info-name"
                >
                    {sellerName}
                </span>
            </div>
            <div className={dt(['ml-8', 'text-base', 'text-black'])}>
                {sellerAddress &&
                    (sellerAddress.street ||
                        sellerAddress.city ||
                        sellerAddress.state ||
                        sellerAddress.zip) && (
                        <div className={dt(['pt-2'])}>
                            {sellerAddress.street && <div>{sellerAddress.street}</div>}
                            {(sellerAddress.city || sellerAddress.state || sellerAddress.zip) && (
                                <div>
                                    {[sellerAddress.city, sellerAddress.state]
                                        .filter(Boolean)
                                        .join(', ')}
                                    {sellerAddress.zip ? ` ${sellerAddress.zip}` : ''}
                                </div>
                            )}
                        </div>
                    )}
                {sellerPhone && (
                    <div className={dt(['pt-2'])}>
                        <a
                            href={`tel:${sellerPhone}`}
                            className={dt(['text-black', 'underline'])}
                            aria-label={`Call seller at ${sellerPhone}`}
                        >
                            {sellerPhone}
                        </a>
                    </div>
                )}
                {sellerEmail && (
                    <div className={dt(['pt-2'])}>
                        <a
                            href={`mailto:${sellerEmail}`}
                            className={dt(['text-black', 'underline'])}
                            aria-label={`Email seller at ${sellerEmail}`}
                        >
                            {sellerEmail}
                        </a>
                    </div>
                )}
                <p
                    className={dt(['text-base', 'mt-4', 'mb-0'])}
                    data-automation-id="seller-reporting-copy"
                >
                    {ProductConstants.SELLER_REPORTING_COPY.before}
                    <span className={dt(['font-open-sans-semibold'])}>
                        {ProductConstants.SELLER_REPORTING_COPY.phone}
                    </span>
                    {ProductConstants.SELLER_REPORTING_COPY.after}
                </p>
            </div>
        </div>
    );
};

const SellerInformation = ({ showHeading = true }) => {
    const featureFlags = useSelector((state) => selectFeatureFlags(state));
    const marketPlaceInfo = useSelector((state) => selectMarketPlaceSellerInfo(false)(state)) || {};

    const isMarketplace = featureFlags.enableMarketPlace && marketPlaceInfo.isMarketPlaceProduct;

    if (!isMarketplace) {
        return null;
    }

    const sellerData = {
        sellerName: marketPlaceInfo.sellerName || null,
        sellerAddress: marketPlaceInfo.sellerAddress || null,
        sellerPhone: marketPlaceInfo.sellerPhone || null,
        sellerEmail: marketPlaceInfo.sellerEmail || null,
    };

    if (!sellerData.sellerName) {
        return null;
    }

    return (
        <ErrorBoundary>
            <div data-automation-id="seller-information-section">
                {showHeading && (
                    <div
                        className={dt([
                            'font-open-sans-semibold',
                            'text-xlarge',
                            'text-black',
                            'pb-3',
                        ])}
                    >
                        {ProductConstants.SELLER_INFORMATION_HEADING}
                    </div>
                )}
                <SellerInformationContent sellerData={sellerData} />
            </div>
        </ErrorBoundary>
    );
};

export default SellerInformation;
